/**
 * Backend API types.
 *
 * These mirror the Prisma schema + Fastify route shapes. When the backend
 * evolves, update these here — keep types co-located with the FE since we
 * don't have a shared schema package yet.
 */

// ── Enums (mirror Prisma) ───────────────────────────────────────────

export type Role = 'MANUFACTURER' | 'INTEGRATOR';

/** Issue #54 — predefined contact roles (dropdown). */
export type ContactRole =
  | 'SECRETARY'
  | 'CEO'
  | 'PRODUCTION_DIRECTOR'
  | 'CHIEF_TECHNOLOGIST'
  | 'ENGINEER'
  | 'OTHER';

export const CONTACT_ROLES: ContactRole[] = [
  'SECRETARY',
  'CEO',
  'PRODUCTION_DIRECTOR',
  'CHIEF_TECHNOLOGIST',
  'ENGINEER',
  'OTHER',
];

export interface Contact {
  id: string;
  clientId: string;
  lastName: string;
  firstName: string;
  middleName: string | null;
  phone: string;
  email: string | null;
  role: ContactRole;
  positionNote: string | null;
  isPrimary: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export function contactFullName(c: Pick<Contact, 'lastName' | 'firstName' | 'middleName'>): string {
  return [c.lastName, c.firstName, c.middleName].filter(Boolean).join(' ');
}

export type OrderStatus =
  | 'DRAFT'
  | 'CONFIRMED'
  | 'IN_PRODUCTION'
  | 'SHIPPED'
  | 'SOLD'
  | 'CANCELLED';

export type InquirySource = 'end_customer' | 'integrator' | 'partner_chkpz' | 'email';

/** Inquiry type directory (issue #22). Nullable on the inquiry. */
export type InquiryType =
  | 'QUESTION'
  | 'REQUEST'
  | 'INVITATION'
  | 'PARTNERSHIP'
  | 'SALE_OFFER'
  | 'GISP'
  | 'AUDIT_REQUEST'
  | 'SOLUTION_HELP';

/**
 * Selectable «Тип запроса» values (lead form + type filter). Legacy values
 * (INVITATION / SALE_OFFER) remain in the union so old rows still render, but
 * they are no longer offered.
 */
export const INQUIRY_TYPES: readonly InquiryType[] = [
  'REQUEST',
  'AUDIT_REQUEST',
  'GISP',
  'QUESTION',
  'SOLUTION_HELP',
  'PARTNERSHIP',
];

// ── Value pipeline (Stage / Confidence / Outcome / Transitions) ─────

export type Stage =
  | 'VALIDATION'
  | 'VERIFICATION'
  | 'PRIORITIZATION'
  | 'REALIZATION'
  | 'INTEGRATOR_SELECTION'
  | 'TKP'
  /**
   * Issue #80 — the industrial route's «Контракт» step (after ТКП, the
   * route's terminal). Educational deals never pass through it: their ТКП
   * step already IS the contract («Контракт» on the board).
   */
  | 'CONTRACT';

export type Confidence = 'K90' | 'K50' | 'K10';

/** Product line — picks the pipeline route. null (legacy) = integration project. */
export type ProductType = 'INTEGRATION_PROJECT' | 'EDUCATIONAL_ROBOT';

/** How the «Концепт проекта» (REALIZATION) work happens — chosen on the
 *  PRIORITIZATION → REALIZATION advance (server-enforced 422 without it). */
export type InteractionMode = 'AUDIT' | 'REMOTE_TZ';

export type InquiryOutcome = 'NONE' | 'WON' | 'LOST';

export type TransitionType =
  | 'CREATED'
  | 'ADVANCE'
  | 'SET_STAGE'
  | 'DROP'
  | 'PAUSE'
  | 'RESUME'
  | 'WIN'
  | 'SET_CONFIDENCE'
  | 'ASSIGN_MANAGER'
  | 'CLIENT_ASSIGN'
  | 'ATTACH'
  | 'COMMENT'
  | 'SET_CRITERION'
  /** Issue #65 — manual «откат на шаг назад» (one stage back). */
  | 'ROLLBACK';

/** Stages in pipeline order, left → right on the board — the industrial
 *  (default) route. Issue #80 appends CONTRACT after TKP. */
export const STAGE_ORDER: readonly Stage[] = [
  'VALIDATION',
  'VERIFICATION',
  'PRIORITIZATION',
  'REALIZATION',
  'INTEGRATOR_SELECTION',
  'TKP',
  'CONTRACT',
];

/** «Учебный робот» route — skips PRIORITIZATION and INTEGRATOR_SELECTION;
 *  issue #80 leaves it untouched: the educational ТКП step already is the
 *  contract, so the route ends at TKP. */
export const EDUCATIONAL_ROUTE: readonly Stage[] = [
  'VALIDATION',
  'VERIFICATION',
  'REALIZATION',
  'TKP',
];

export function routeStages(productType: ProductType | null | undefined): readonly Stage[] {
  return productType === 'EDUCATIONAL_ROBOT' ? EDUCATIONAL_ROUTE : STAGE_ORDER;
}

export function nextStageFor(stage: Stage, productType: ProductType | null | undefined): Stage | null {
  const route = routeStages(productType);
  const idx = route.indexOf(stage);
  if (idx < 0 || idx >= route.length - 1) return null;
  return route[idx + 1];
}

/** Legal ADVANCE targets for (stage, productType): route next + the single
 *  allowed skip — educational «Нет, перейти к ТКП» (VERIFICATION → TKP). */
export function allowedAdvanceTargets(
  stage: Stage,
  productType: ProductType | null | undefined,
): Stage[] {
  const next = nextStageFor(stage, productType);
  if (!next) return [];
  return productType === 'EDUCATIONAL_ROBOT' && stage === 'VERIFICATION'
    ? [next, 'TKP']
    : [next];
}

/** Issue #65 — the stage one step BACK along the deal's route, or null when the
 *  deal is already on the first stage (rollback unavailable). */
export function previousStageFor(
  stage: Stage,
  productType: ProductType | null | undefined,
): Stage | null {
  const route = routeStages(productType);
  const idx = route.indexOf(stage);
  if (idx <= 0) return null;
  return route[idx - 1];
}

/** Stages where a confidence rating is mandatory (server-enforced 422). */
export const CONFIDENCE_REQUIRED_STAGES: readonly Stage[] = [
  'PRIORITIZATION',
  'REALIZATION',
  'INTEGRATOR_SELECTION',
  'TKP',
  'CONTRACT',
];

/** Next stage in the pipeline, or null at CONTRACT (terminal of the ladder). */
export function nextStage(stage: Stage): Stage | null {
  const idx = STAGE_ORDER.indexOf(stage);
  return idx >= 0 && idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : null;
}

// ── Issue #80 — «Параметры сделки» (the «Контракт» step block) ───────

/** «Скидка / Полная стоимость» plate; null = not picked yet (greyed out). */
export type DiscountMode = 'DISCOUNT' | 'FULL_PRICE';

/** «Позиции» — one row per robot type in the contract. Free text for now;
 *  later a pick from the «Каталог» list. */
export interface DealPosition {
  robotType: string;
  quantity: number;
}

/** «Аванс»/«Оплата» rows — the deal may be paid in several steps. */
export interface DealPaymentRow {
  amount: number;
  /** When the money arrived; null = planned. */
  paidAt: string | null;
}

/** «Дата отгрузки» rows — a previously entered position label or free text. */
export interface DealShipmentRow {
  positionLabel: string;
  shipAt: string | null;
}

/** Issue #80 — the stored shape of the «Параметры сделки» block: one JSON
 *  payload on the inquiry, always written as a whole (PATCH replaces it).
 *  Industrial deals use the full cost breakdown (Робот / РТК / иные
 *  услуги); the educational variant keeps it simple — positions +
 *  totalContractCost + deliveryDate. */
export interface DealParams {
  positions: DealPosition[];
  discountMode: DiscountMode | null;
  totalContractCost: number | null;
  robotCost: number | null;
  rtcCost: number | null;
  otherCost: number | null;
  payments: DealPaymentRow[];
  shipment: DealShipmentRow[];
  /** «Дата интеграции РТК» rows — non-blocking. */
  integrationDates: string[];
  /** «Дата поставки» — the simple educational variant. */
  deliveryDate: string | null;
  /** «Дата закрытия сделки» — non-blocking. */
  closingAt: string | null;
}

export interface InquiryTransition {
  id: string;
  inquiryId: string;
  type: TransitionType;
  fromStage: Stage | null;
  toStage: Stage | null;
  confidence: Confidence | null;
  reason: string | null;
  /** true when the pause-reminder scheduler emitted this RESUME (issue #25). */
  fromPause?: boolean;
  /** COMMENT body text (issue #44) — `reason` carries the title. */
  note?: string | null;
  /** ATTACH entries link the artifact for download from history (issue #43). */
  attachmentId?: string | null;
  /** SET_CRITERION entries carry the 1..5 value; `reason` holds the criterion key (issue #26). */
  criterionScore?: number | null;
  actorId: string;
  createdAt: string;
  actor?: Pick<User, 'id' | 'name'>;
}

// ── Attachments (issue #27) ───────────────────────────────────────

export type CriteriaBasket = 'GREEN' | 'YELLOW' | 'RED';

export interface InquiryCriterionEntry {
  key: string;
  labelRu: string;
  labelEn: string;
  order: number;
  score: number | null;
  note: string | null;
  updatedAt: string | null;
}

export interface InquiryCriteriaAggregate {
  average: number | null;
  filled: number;
  total: number;
  basket: CriteriaBasket | null;
}

export interface InquiryCriteriaResponse {
  criteria: InquiryCriterionEntry[];
  aggregate: InquiryCriteriaAggregate;
}

export type AttachmentKind = 'CONCEPT' | 'TKP' | 'REPORT' | 'OTHER';

export const ATTACHMENT_KINDS: readonly AttachmentKind[] = ['CONCEPT', 'TKP', 'REPORT', 'OTHER'];

export interface InquiryAttachment {
  id: string;
  inquiryId: string;
  filename: string;
  mimeType: string;
  size: number;
  kind: AttachmentKind;
  title?: string | null;
  description?: string | null;
  createdAt: string;
  uploadedBy?: Pick<User, 'id' | 'name'>;
}

// ── Entities ────────────────────────────────────────────────────────

export interface User {
  id: string;
  email: string;
  name: string;
  role: Role;
  integratorId: string | null;
  integrator?: Integrator | null;
  /** Issue #66 — «Менеджер» flag (from /auth/me); drives the leads-tab filters. */
  isManager?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Integrator {
  id: string;
  name: string;
  contactEmail: string;
  contactPhone: string;
  priority: number;
  specialization: string | null;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface Client {
  id: string;
  companyName: string;
  inn: string | null;
  website: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  industry: string | null;
  notes: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Booking {
  id: string;
  clientId: string;
  integratorId: string;
  bookedById: string;
  active: boolean;
  bookedAt: string;
  client?: Pick<Client, 'id' | 'companyName'>;
  integrator?: Pick<Integrator, 'id' | 'name'>;
}

export interface Inquiry {
  id: string;
  /** Issue #56 — persistent sequential display number (RF-0001+), assigned by the DB. */
  ref: number;
  clientId: string | null;
  source: InquirySource;
  channel: string | null;
  /** Issue #63 — optional card title; empty falls back to the client name. */
  cardTitle?: string | null;
  sourceEmail?: string | null;
  summary: string;
  type?: InquiryType | null;
  stage: Stage;
  confidence: Confidence | null;
  confidenceReason?: string | null;
  /** Work mode picked on the PRIORITIZATION → REALIZATION advance. */
  interactionMode?: InteractionMode | null;
  /** Optional on-site audit date (only with interactionMode = 'AUDIT'). */
  auditAt?: string | null;
  /** Product line — drives the pipeline route (educational skips
   *  PRIORITIZATION and INTEGRATOR_SELECTION). null = integration project. */
  productType?: ProductType | null;
  /** What was proposed to the client — recorded on the REALIZATION →
   *  INTEGRATOR_SELECTION advance (a CONCEPT artifact also covers it). */
  conceptSummary?: string | null;
  /** Optional flag — the client approved the concept; does not gate the advance. */
  conceptApprovedByClient?: boolean;
  paused: boolean;
  pauseReason?: string | null;
  /** Optional reminder date for a paused deal (issue #25). */
  pauseRemindAt?: string | null;
  /** Planned next-action date set by the manager (issue #52). */
  nextStepAt?: string | null;
  outcome: InquiryOutcome;
  outcomeReason?: string | null;
  outcomeAt?: string | null;
  estimatedAmount?: number | string | null;
  createdById: string;
  assignedIntegratorId: string | null;
  /** Issue #59 — integrators picked on the lead form for the TKP (ТКП) calculation. */
  tkpIntegratorIds?: string[];
  /** Issue #78 — «Генподрядчик»: the company that ends up the final service
   *  provider. Auto-filled for educational robots on the concept step;
   *  industrial deals set it by hand from that step on. */
  generalContractor?: string | null;
  /** Issue #80 — «Параметры сделки» (the «Контракт» step block): one JSON
   *  payload — positions, cost breakdown, payments, dates. PATCH replaces
   *  the whole block; null = not filled in. */
  dealParams?: DealParams | null;
  assignedManagerId?: string | null;
  createdAt: string;
  updatedAt: string;
  client?: Pick<Client, 'id' | 'companyName' | 'contactEmail'> | null;
  createdBy?: Pick<User, 'id' | 'name'>;
  assignedIntegrator?: Pick<Integrator, 'id' | 'name'> | null;
  assignedManager?: Pick<User, 'id' | 'name'> | null;
  /** List endpoint carries the latest transition (for the «из заморозки» badge, #25). */
  transitions?: InquiryTransition[];
}

export interface RobotModel {
  id: string;
  sku: string;
  name: string;
  description: string | null;
  basePrice: number;
  active: boolean;
  /** Product line — drives which pipeline route the model is sold into. */
  productType: ProductType;
  createdAt?: string;
  updatedAt?: string;
}

export interface Order {
  id: string;
  clientId: string;
  integratorId: string;
  robotModelId: string;
  quantity: number;
  unitPrice: number;
  status: OrderStatus;
  createdById: string;
  soldAt: string | null;
  licenseSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
  client?: Pick<Client, 'id' | 'companyName'>;
  integrator?: Pick<Integrator, 'id' | 'name'>;
  robotModel?: Pick<RobotModel, 'id' | 'sku' | 'name'>;
}

// ── Status display helpers (UI-side mapping) ───────────────────────
// Human-readable order status labels live in i18n (`status.order.*`).

/** Status pill colour key — maps to --color-status-* / --color-order-* CSS tokens. */
export function orderStatusColor(s: OrderStatus): string {
  switch (s) {
    case 'DRAFT':         return 'draft';
    case 'CONFIRMED':     return 'confirmed';
    case 'IN_PRODUCTION': return 'production';
    case 'SHIPPED':       return 'shipped';
    case 'SOLD':          return 'won';        // reuse success
    case 'CANCELLED':     return 'rejected';   // reuse danger
  }
}

/** Parse estimatedAmount (Prisma Decimal serialises as string) to a number. */
export function inquiryAmount(inq: Pick<Inquiry, 'estimatedAmount'>): number | null {
  const raw = inq.estimatedAmount;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * Issue #50 — a ₽ amount may surface on board/list only from «ТКП»: before
 * that stage any number is noise (at «Концепт проекта» it is at best a rough
 * robot-only estimate, which Kuanysh asked not to show), from ТКП — the first
 * real project estimate.
 */
export function stageShowsAmount(stage: Stage): boolean {
  return STAGE_ORDER.indexOf(stage) >= STAGE_ORDER.indexOf('TKP');
}

/**
 * Issue #80 — the «Параметры сделки» block lives on the «Контракт» step of
 * the deal's route: industrial deals on CONTRACT (their contract step),
 * educational deals on TKP — their ТКП step already IS the contract.
 * Earlier steps never show the block (the PATCH gate accepts writes from
 * REALIZATION on, but the UI surfaces the block only at the contract — a
 * deliberate slice-2 decision, recorded in the MR).
 */
export function stageShowsDealParams(
  stage: Stage,
  productType: ProductType | null | undefined,
): boolean {
  return productType === 'EDUCATIONAL_ROBOT' ? stage === 'TKP' : stage === 'CONTRACT';
}

/**
 * Issue #78 — the built-in general contractor for educational-robot deals.
 * Same string as the server constant (server/src/routes/pipeline.ts) — there
 * is no shared package between web and server, so it is mirrored here.
 */
export const EDUCATIONAL_GENERAL_CONTRACTOR = 'ООО «Завод Роботов»';

/**
 * Issue #78 — the «Генподрядчик» block exists from the concept («Концепт/ТКП»,
 * REALIZATION) step on; before that the field is not shown at all.
 */
export function stageShowsGeneralContractor(stage: Stage): boolean {
  return STAGE_ORDER.indexOf(stage) >= STAGE_ORDER.indexOf('REALIZATION');
}

/**
 * Issue #78 — display value for the «Генподрядчик» block. Educational deals
 * always show the auto-assigned default (a stored value wins); industrial
 * deals show the stored value or null («Не указан» is up to the caller).
 */
export function generalContractorDisplay(
  inq: Pick<Inquiry, 'generalContractor' | 'productType'>,
): string | null {
  if (inq.generalContractor) return inq.generalContractor;
  return inq.productType === 'EDUCATIONAL_ROBOT' ? EDUCATIONAL_GENERAL_CONTRACTOR : null;
}

/**
 * Issue #51 — the day counter surfaces only from «ТКП» on: earlier stages are
 * trust-building, and a raw day count there pushes managers to chase clients.
 */
export function stageShowsTimer(stage: Stage): boolean {
  return STAGE_ORDER.indexOf(stage) >= STAGE_ORDER.indexOf('TKP');
}

/**
 * Amount allowed for display on the kanban board and list view: null for early
 * stages, null when no amount is set — never a «0 ₽» placeholder (#50).
 */
export function visibleInquiryAmount(
  inq: Pick<Inquiry, 'estimatedAmount' | 'stage'>,
): number | null {
  if (!stageShowsAmount(inq.stage)) return null;
  const n = inquiryAmount(inq);
  return n !== null && n > 0 ? n : null;
}

/**
 * Short display id for cards/lists: the persistent sequential ref (RF-0001).
 * Graceful fallback to the legacy cuid-derived form (RF-8F3K) when `ref` is
 * missing (stale caches / pre-migration payloads).
 */
/**
 * Issue #63 — the card title: the deal's own `cardTitle` when set, otherwise
 * the client's company name (the previous behaviour). Returns null when neither
 * is available, so callers keep their own «no client» fallback.
 */
export function inquiryTitle(
  inq: Pick<Inquiry, 'cardTitle'> & { client?: Pick<Client, 'id' | 'companyName'> | null },
): string | null {
  const title = inq.cardTitle?.trim();
  if (title) return title;
  return inq.client?.companyName ?? null;
}

export function inquiryRef(inq: Pick<Inquiry, 'id'> & { ref?: number | null }): string {
  return inq.ref != null
    ? `RF-${String(inq.ref).padStart(4, '0')}`
    : `RF-${inq.id.slice(-4).toUpperCase()}`;
}

// ── Pipeline reports (GET /api/reports/pipeline*) ───────────────────

/** Per-stage metrics returned by GET /api/reports/pipeline. */
export interface StageMetrics {
  stage: Stage;
  /** Events that moved a deal INTO the stage within the period
   *  (toStage=X, fromStage≠X|null — incl. reverse moves back). Explicit, not a net diff. */
  in: number;
  /** Events that moved a deal OUT of the stage within the period
   *  (fromStage=X, toStage≠X|null — forward ADVANCE, DROP, returns). Explicit —
   *  in and out need not reconcile arithmetically because of returns. */
  out: number;
  /** ADVANCE events out of the stage within the period. */
  advanced: number;
  /** DROP events out of the stage within the period. */
  dropped: number;
  /** PAUSE events within the period. */
  paused: number;
  /** Open deals of this stage currently on pause (snapshot). */
  currentlyPaused: number;
  /** Current open portfolio at this stage, by confidence basket. */
  byConfidence: Record<Confidence | 'none', number>;
  /** Sum of estimatedAmount over current open deals at this stage. */
  sum: number;
}

export interface StageConversion {
  from: Stage;
  to: Stage;
  /** advanced/in of the `from` stage, 0..1 (0 when denominator is 0). */
  rate: number;
}

export interface PortfolioBucket {
  count: number;
  sum: number;
}

/** Base conversion «Входящие запросы → stage»: share of the period's
 *  VALIDATION cohort that reached the stage (route-aware denominator). */
export interface BaseConversion {
  stage: Stage;
  base: number;
  reached: number;
  /** reached/base, 0..1 (0 when denominator is 0). */
  rate: number;
}

/** Response shape of GET /api/reports/pipeline. */
export interface PipelineReport {
  from: string;
  to: string;
  /**
   * Issue #60 — data scope of this report: `all` = company-wide («Руководитель»),
   * `own` = the caller's own deals only («Менеджер»). Enforced server-side.
   */
  scope?: 'all' | 'own';
  stages: StageMetrics[];
  conversion: StageConversion[];
  baseConversion: BaseConversion[];
  portfolio: Record<Confidence | 'none', PortfolioBucket>;
}

/** Drill-in group for GET /api/reports/pipeline/group. */
export type DrillInGroup = 'current' | 'in' | 'out' | 'advanced' | 'dropped' | 'paused';

/** Summary block of GET /api/reports/stage/:stage. */
export interface StageAnalyticsSummary {
  /** Deals that entered the stage in the period (incl. returns). */
  in: number;
  /** Deals that left the stage in the period (excl. pause self-loops). */
  out: number;
  /** Of the exits: moved forward (ADVANCE, or SET_STAGE to a later stage). */
  advanced: number;
  /** Of the exits: dropped. */
  dropped: number;
  /** PAUSE events at the stage in the period (self-loops, not exits). */
  paused: number;
  /** Of the exits: SET_STAGE back to an earlier stage. */
  returned: number;
  /** Open deals at the stage right now (snapshot at end of period). */
  current: number;
  /** Sum of estimatedAmount over the current open deals. */
  currentSum: number;
  /** Of the current deals: on pause. */
  currentPaused: number;
  /** Average age (days, 1 decimal) of the current open deals; null when empty. */
  avgAgeDays: number | null;
  /** Next stage in the ladder, null at TKP. */
  nextStage: Stage | null;
  /** advanced/in capped at 1; null when nothing entered the stage. */
  conversionToNext: number | null;
}

/** One row of a transition-direction breakdown (stage drill-in). */
export interface StageBreakdownItem {
  /** Stage name, or pseudo-key: NEW | PAUSE | DROPPED | PAUSED | WON. */
  key: string;
  count: number;
}

/** Response shape of GET /api/reports/stage/:stage. */
export interface StageAnalytics {
  stage: Stage;
  from: string;
  to: string;
  summary: StageAnalyticsSummary;
  breakdownIn: StageBreakdownItem[];
  breakdownOut: StageBreakdownItem[];
}
